import { generate, correction, mode } from 'lean-qr'
import {
  extrudeContours,
  countExtrudeTris,
  withWinding,
  dataModuleSquare,
  roundedRingContour,
  roundedSquare,
} from './stlExport'
import type { QrMatrix, Contour } from './stlExport'

// A spray-paint stencil is the *inverse* of the raised-module STL: a thin
// plate with holes where paint should land. Two constraints shape the design:
//
// 1. No islands — any hole that fully encloses material would make that
//    material fall out. Data modules are therefore cut as isolated circles
//    (never touching), and the three finder-pattern "eye" rings are cut as
//    C-shaped contours with small bridges so the ring interior stays
//    attached to the plate.
// 2. The stencil ignores visual style settings (colors, dot shape) — the
//    sprayed result is determined purely by hole geometry. Only the URL
//    matters (error-correction is fixed at L), so the QR matrix is generated
//    directly from lean-qr rather than scraped from the styled SVG.
//
// Both the plaque and stencil use identical contour geometry (same circle
// radius, same C-shaped finder ring with bridge, same rounded center square)
// so the two models are exact geometric inverses of each other.
//
// URL simplification: uppercasing the entire URL (including "HTTPS://") keeps
// lean-qr in alphanumeric mode (5.5 bits/char vs 8 for byte mode) — all
// characters in "HTTPS://H3R.NO/A3KX9P" are in the alphanumeric charset
// (0–9 A–Z $%*+-./:). The 21-character payload fits QR version 1 (21×21
// modules) at error-correction level L (7% error tolerance). Keeping the
// HTTPS:// scheme means phones navigate directly rather than searching.
// Version 1 has no alignment pattern, simplifying the geometry considerably.

// Physical dimensions (mm)
const QR_SIZE_MM = 50        // QR content — matches the raised-module STL
const BORDER_MM = 1          // solid frame: minimal — user adds their own margin
const THICKNESS_MM = 1.2     // thin = sharp spray edges, still rigid with frame

// Re-export for test access
export type { QrMatrix, Contour }

/**
 * Build all stencil contours in mm. Index 0 is the plate outline; the rest
 * are holes. Windings are normalized for extrudeContours.
 */
export function buildStencilContours(matrix: QrMatrix): Contour[] {
  const n = matrix.size
  const m = QR_SIZE_MM / n
  const plate = QR_SIZE_MM + 2 * BORDER_MM

  const finderOrigins: [number, number][] = [
    [0, 0],
    [n - 7, 0],
    [0, n - 7],
  ]
  const inFinder = (x: number, y: number) =>
    finderOrigins.some(([fx, fy]) => x >= fx && x < fx + 7 && y >= fy && y < fy + 7)

  // Version 1 (21×21) has no alignment pattern. generateStencilStl caps
  // maxVersion at 1, so we never need to render one here.

  const holes: Contour[] = []

  // Data modules (everything outside the three finder eyes) → isolated rounded
  // squares. Adjacent modules are intentionally kept separate (DOT_FILL_RATIO
  // < 1.0): merging them into blob contours changes the QR pattern enough to
  // break scanner decode.
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (!matrix.get(x, y) || inFinder(x, y)) continue
      holes.push(dataModuleSquare(
        BORDER_MM + (x + 0.5) * m,
        BORDER_MM + (y + 0.5) * m,
        m,
      ))
    }
  }

  // Finder eyes → bridged rounded-square ring (7×7, outer corner radius 1.5
  // modules, cut 2 modules from left corner) + rounded-square 3×3 center.
  // Empirically tuned against jsQR: cuts at 2.5 modules (too close to the
  // center-cross check lines) or outer radius ≥ 2 modules both break
  // detection; R 1.5 / cut 2.0 passes with margin on both parameters.
  for (const [fx, fy] of finderOrigins) {
    const ox = BORDER_MM + fx * m
    const oy = BORDER_MM + fy * m
    const translate = (c: Contour): Contour => c.map(([x, y]) => [ox + x, oy + y])
    holes.push(translate(roundedRingContour(7 * m, m, 1.5 * m, 2 * m)))
    holes.push(roundedSquare(ox + 2 * m, oy + 2 * m, 3 * m, m))
  }

  // Outer plate with rounded corners matching the finder eye outer radius (1.5
  // modules). roundedSquare origin is top-left in Y-down space.
  const eyeCornerRadius = 1.5 * m
  const outer: Contour = roundedSquare(0, 0, plate, eyeCornerRadius)

  // Flip Y so the STL has Y pointing up (matching standard 3D viewer convention)
  // and the bridge on the top edge of each eye appears at the top in the viewer.
  const flipY = (c: Contour): Contour => c.map(([x, y]) => [x, plate - y])

  return [
    withWinding(flipY(outer), true),
    ...holes.map((h) => withWinding(flipY(h), false)),
  ]
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Convert a full gallery URL to the alphanumeric form used in the stencil QR
 * code. Uppercasing the entire URL (including the scheme) keeps lean-qr in
 * alphanumeric mode — all characters in the result are in the QR alphanumeric
 * charset (0–9 A–Z $%*+-./:). The HTTPS:// prefix is preserved so phones
 * navigate directly instead of searching.
 *
 * Example: "https://h3r.no/a3kx9p" → "HTTPS://H3R.NO/A3KX9P"
 */
export function toStencilUrl(galleryUrl: string): string {
  return galleryUrl.toUpperCase()
}

export function generateStencilStl(url: string): ArrayBuffer {
  // Encode in alphanumeric mode (5.5 bits/char vs 8 for byte) — valid because
  // the URL has been fully uppercased by toStencilUrl() and all characters
  // are in the QR alphanumeric charset.
  // maxVersion 1 locks the output to a 21×21 matrix (no alignment pattern).
  // EC level L is used: the 21-character HTTPS:// payload exactly fits v1 at
  // L (capacity 25 chars). L provides 7% error tolerance — less than Q, but
  // keeping HTTPS:// means phones navigate directly rather than searching.
  const matrix = generate(mode.alphaNumeric(url), {
    minCorrectionLevel: correction.L,
    maxCorrectionLevel: correction.L,
    maxVersion: 1,
  })
  const contours = buildStencilContours(matrix)

  // Single closed solid: plate outline extruded with every hole punched through.
  const totalTris = countExtrudeTris(contours)
  const buffer = new ArrayBuffer(80 + 4 + totalTris * 50)
  const view = new DataView(buffer)

  const header = 'QR stencil generated by h3r.no'
  for (let i = 0; i < Math.min(header.length, 80); i++) view.setUint8(i, header.charCodeAt(i))
  view.setUint32(80, totalTris, true)

  extrudeContours(view, 84, contours, 0, THICKNESS_MM)

  return buffer
}

export function downloadStencilStl(url: string, filename = 'qr-code'): void {
  const buffer = generateStencilStl(toStencilUrl(url))
  const blob = new Blob([buffer], { type: 'model/stl' })
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = `${filename}-stencil.stl`
  a.click()
  URL.revokeObjectURL(href)
}
